import {
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from "@tanstack/react-query";

import { useServer } from "../../server/ServerContext";
import { PortalAxiosError } from "../../types";
import { ProgramData, ProgramsData, ProgramVariables } from "./types";
import { getProgramVariables, programsKeys } from "./utils";

/**
 * Fetch a single program a user has enrolled in
 * @param programIdentifier program identifier
 * @param options useQuery options
 * @returns useQuery result
 */
export const useProgram = <TData extends {} = ProgramData>(
  variables: ProgramVariables,
  options?: UseQueryOptions<ProgramData, PortalAxiosError, TData>
) => {
  const server = useServer();
  const queryClient = useQueryClient();

  const { programIdentifier, params } = getProgramVariables(variables);

  return useQuery<ProgramData, PortalAxiosError, TData>(
    programsKeys.getOne(programIdentifier),
    () => server.get(`programs/${programIdentifier}`, { params }),
    {
      placeholderData: () => {
        const programsData = queryClient.getQueryData<ProgramsData>(
          programsKeys.list
        );
        const program = programsData?.data.find((program) => {
          return program.identifier === programIdentifier;
        });

        return program ? { data: program } : undefined;
      },
      staleTime: 2 * 60 * 1000,
      ...options,
    }
  );
};

export default useProgram;
